import React, { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es as esDateLocale } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Users, Clock, Trophy, ArrowLeft, User, Share2, KeyRound, MessageCircle } from "lucide-react";
import AccessCodeEntry from "./registration/AccessCodeEntry";

import heroPadel from "@/assets/hero-padel.jpg";
import heroTennis from "@/assets/hero-tennis.jpg";
import heroPickleball from "@/assets/hero-pickleball.jpg";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import TournamentRoundRobinView from "./tournament/TournamentRoundRobinView";
import TournamentEliminationView from "./tournament/TournamentEliminationView";
import TournamentEliminationMatchesView from "./tournament/TournamentEliminationMatchesView";
import TournamentSumGamesView from "./tournament/TournamentSumGamesView";
import TournamentInfo from "./tournament/TournamentInfo";
import EventRegistrationForm from "./EventRegistrationForm";
import PendingStateCard from "./PendingStateCard";
import { TournamentProgressCard } from "./tournament/TournamentProgressCard";
import PendingMatchCard from "./PendingMatchCard";
import PendingRankingTable from "./PendingRankingTable";
import PendingBracketView from "./PendingBracketView";
import { useEvents } from "@/hooks/useEvents";
import { getTournamentById, getNextMatchInfo, formatNextMatchDisplay } from "@/utils/eventUtils";
import { generateTournamentData } from "@/utils/tournamentDataGenerator";
import { MASTER_EVENTS, generateConsistentEventData } from "@/utils/masterEventData";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserRegistration } from "@/hooks/useUserRegistration";
import { useLanguage } from "@/contexts/LanguageContext";

interface Match {
  id: number;
  round: string;
  player1: string;
  player2: string;
  score?: string;
  court?: string;
  time?: string;
  date?: string;
  status: 'scheduled' | 'in-progress' | 'completed';
  isUserMatch?: boolean;
}

interface TournamentDetailsProps {
  tournamentId: string;
  onBack: () => void;
  showRegistration?: boolean;
  onContactOrganizer?: () => void;
}

const TournamentDetails = ({ tournamentId, onBack, showRegistration: initialShowRegistration = false, onContactOrganizer }: TournamentDetailsProps) => {
  const isMobile = useIsMobile();
  const { registration, registrations, loading: registrationLoading } = useUserRegistration(tournamentId);
  const [showRegistration, setShowRegistration] = useState(initialShowRegistration);
  const [showAccessCodeEntry, setShowAccessCodeEntry] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const { t, dateLocale, language } = useLanguage();
  // Handler para iniciar proceso de inscripción
  const handleRegisterClick = () => {
    setShowRegistration(true);
    setShowAccessCodeEntry(false);
    setTimeout(() => {
      const registrationElement = document.getElementById('registration-section');
      if (registrationElement) {
        registrationElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // Handler para mostrar entrada de código de acceso
  const handleAccessCodeClick = () => {
    setShowAccessCodeEntry(true);
    setShowRegistration(false);
    setTimeout(() => {
      const registrationElement = document.getElementById('registration-section');
      if (registrationElement) {
        registrationElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // State to track current view for dynamic rules
  const [currentView, setCurrentView] = useState<'groups' | 'elimination'>('groups');
  // State for managing brackets view in pending round-robin
  const [showPendingBrackets, setShowPendingBrackets] = useState<boolean>(false);
  
  // Normalize tournament format to use hyphens instead of underscores
  const normalizeTournamentFormat = (format?: string): string => {
    if (!format) return 'round-robin';
    const normalized = format.replace(/_/g, '-');
    if (normalized === 'elimination') return 'eliminacion-directa';
    if (normalized === 'sum-games') return 'sumatoria-juegos';
    return normalized;
  };
  // Get tournament data from Supabase events and master data for fallback
  const { events, loading: eventsLoading } = useEvents();
  
  // Show skeleton while events are loading
  if (eventsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079]">
        <div className="relative w-full h-40 md:h-48 bg-muted animate-pulse" />
        <div className="p-4 space-y-4">
          <div className="h-6 bg-muted rounded w-3/4 animate-pulse" />
          <div className="h-4 bg-muted rounded w-1/2 animate-pulse" />
          <div className="space-y-3 mt-6">
            <div className="h-4 bg-muted rounded w-full animate-pulse" />
            <div className="h-4 bg-muted rounded w-4/5 animate-pulse" />
            <div className="h-4 bg-muted rounded w-2/3 animate-pulse" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="h-24 bg-muted rounded-lg animate-pulse" />
            <div className="h-24 bg-muted rounded-lg animate-pulse" />
          </div>
          <div className="space-y-3 mt-6">
            <div className="h-32 bg-muted rounded-lg animate-pulse" />
            <div className="h-32 bg-muted rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }
  const supabaseEvent = events.find(e => e.id === tournamentId);
  const masterEvent = MASTER_EVENTS.find(event => event.id === parseInt(tournamentId) || event.id.toString() === tournamentId);
  
  // Create a default event for UUID events that don't exist in master data
  const createDefaultEvent = (id: string) => ({
    id,
    title: "Torneo de Pádel Primavera",
    sport: "Pádel",
    startDate: "2025-09-15",
    endDate: "2025-09-17",
    time: "10:00",
    location: "Centro Deportivo Madrid",
    organizer: "Club Las Raquetas",
    registrationFee: 45,
    maxParticipants: 24,
    participants: 16,
    categories: ["2", "3"],
    genderCategories: ["Mixtos"],
    subcategories: { "Mixtos": ["A", "B"] },
    matchType: "dobles" as const,
    tournamentFormat: "round-robin" as const,
    status: "upcoming" as const,
    isLeague: false,
    prizes: [{
      category: "Mixtos",
      subcategory: "A", 
      firstPlace: [{ type: "trofeo" as const }, { type: "dinero" as const, amount: 200 }]
    }]
  });
  
  // Convert Supabase event to expected format or use master event/default as fallback
  const eventData = supabaseEvent ? {
    id: supabaseEvent.id,
    title: supabaseEvent.title,
    sport: supabaseEvent.sport,
    location: supabaseEvent.location,
    startDate: supabaseEvent.start_date,
    endDate: supabaseEvent.end_date,
    registrationFee: supabaseEvent.entry_fee || 0,
    category: supabaseEvent.category || 'Categoría 2',
    subcategory: supabaseEvent.subcategory || 'A',
    genderCategories: (supabaseEvent.genderCategories && supabaseEvent.genderCategories.length > 0) 
      ? supabaseEvent.genderCategories 
      : ['Mixtos'],
    subcategories: (supabaseEvent.subcategories && Object.keys(supabaseEvent.subcategories).length > 0) 
      ? supabaseEvent.subcategories 
      : { "Mixtos": ["A", "B"] },
    matchType: supabaseEvent.match_type || 'dobles',
    tournamentFormat: normalizeTournamentFormat(supabaseEvent.tournament_format),
    isLeague: supabaseEvent.event_type === 'league',
    status: supabaseEvent.status,
    organizer: (supabaseEvent as any).profiles?.full_name || (supabaseEvent as any).organizer_name || 'Club Deportivo Elite',
    time: '09:00',
    partner: registration?.partner_name || undefined,
    currentStage: 'Fase de Grupos',
    maxParticipants: supabaseEvent.max_participants || 24,
    participants: 16,
    imageUrl: supabaseEvent.image_url || null,
    address: supabaseEvent.address,
    postal_code: supabaseEvent.postal_code,
    country: (supabaseEvent as any).country,
    state: (supabaseEvent as any).state,
    prizes: [{
      category: "Mixtos",
      subcategory: "A", 
      firstPlace: [
        { type: "trofeo" as const }, 
        { type: "dinero" as const, amount: 300 }
      ],
      secondPlace: [
        { type: "medalla" as const }, 
        { type: "dinero" as const, amount: 150 }
      ]
    }],
    rules: (() => {
      let parsedRules: any = {};
      if (supabaseEvent.rules) {
        try {
          parsedRules = typeof supabaseEvent.rules === 'string' 
            ? JSON.parse(supabaseEvent.rules) 
            : supabaseEvent.rules;
        } catch (e) {
          console.error('Error parsing supabase rules:', e);
        }
      }
      
      return {
        format: normalizeTournamentFormat(supabaseEvent.tournament_format),
        sets: 'Mejor de 3 sets',
        scoring: 'Puntuación estándar de pádel',
        tiebreak: 'Tiebreak a 7 puntos',
        timing: 'Calentamiento: 5 minutos',
        conduct: 'Respeto hacia árbitros y rivales obligatorio',
        genderCategories: parsedRules.genderCategories || null,
        selectedGenders: parsedRules.selectedGenders || null,
        playersPerGroup: parsedRules.playersPerGroup || 3,
        groupRules: parsedRules.groupRules || null,
        finalRules: parsedRules.finalRules || null
      };
    })()
  } : masterEvent || createDefaultEvent(tournamentId);
  
  // Generate consistent data for the tournament
  const consistentData = generateConsistentEventData(
    parseInt(tournamentId) || 1,
    eventData?.sport || 'Pádel',
    (eventData?.matchType === 'singles' || eventData?.matchType === 'dobles' || eventData?.matchType === 'ambos') ? eventData.matchType : 'dobles',
    false
  );
  
  console.log('TournamentDetails - Master event:', masterEvent);
  console.log('TournamentDetails - Consistent data:', consistentData);
  
  const formatCategoryDisplay = (category: string | null, subcategory: string | null): string | null => {
    if (!category) return null;
    if (!subcategory || subcategory === category) return category;
    return `${category} ${subcategory}`;
  };

  const tournament = {
    id: eventData?.id || tournamentId,
    title: eventData?.title || masterEvent?.title || "Torneo Mixto Primavera",
    organizer: eventData?.organizer || masterEvent?.organizer || "Club Las Raquetas",
    sport: eventData?.sport || masterEvent?.sport || "Pádel",
    startDate: (eventData as any)?.startDate || masterEvent?.date || "2025-06-15",
    endDate: eventData?.endDate || masterEvent?.endDate || "2025-06-17",
    registrationFee: eventData?.registrationFee || 0,
    time: eventData?.time || masterEvent?.time || "09:00",
    location: eventData?.location || masterEvent?.location || "Club Las Raquetas",
    category: formatCategoryDisplay(registration?.category, registration?.subcategory) || (eventData as any)?.category || null,
    subcategory: registration?.subcategory || (eventData as any)?.subcategory || null,
    genderCategories: (eventData?.genderCategories && Array.isArray(eventData.genderCategories) && eventData.genderCategories.length > 0) 
      ? eventData.genderCategories 
      : [],
    subcategories: (eventData?.subcategories && typeof eventData.subcategories === 'object' && Object.keys(eventData.subcategories).length > 0) 
      ? eventData.subcategories 
      : {},
    matchType: (eventData?.matchType === 'singles' || eventData?.matchType === 'dobles') ? eventData.matchType : 'dobles',
    isLeague: eventData?.isLeague || false,
    partner: registration?.partner_name || (eventData as any)?.partner || null,
    imageUrl: (eventData as any)?.imageUrl || null,
    prizes: (eventData as any)?.prizes || masterEvent?.prizes || [{
      category: "Mixtos",
      subcategory: "A", 
      firstPlace: [{ type: "trofeo" as const }, { type: "dinero" as const, amount: 250 }],
      secondPlace: [{ type: "medalla" as const }, { type: "dinero" as const, amount: 125 }]
    }],
    rules: (eventData as any)?.rules || (() => {
      let parsedRules: any = {};
      if (supabaseEvent?.rules) {
        try {
          parsedRules = typeof supabaseEvent.rules === 'string' 
            ? JSON.parse(supabaseEvent.rules) 
            : supabaseEvent.rules;
        } catch (e) {
          console.error('Error parsing fallback supabase rules:', e);
        }
      }
      
      return {
        format: 'round-robin',
        sets: 'Mejor de 3 sets',
        scoring: 'Puntuación estándar',
        tiebreak: 'Tiebreak a 7 puntos',
        timing: 'Calentamiento: 5 minutos',
        conduct: 'Respeto hacia árbitros y rivales',
        genderCategories: parsedRules.genderCategories || null,
        selectedGenders: parsedRules.selectedGenders || null,
        playersPerGroup: parsedRules.playersPerGroup || 3
      };
    })(),
     status: (() => {
       if ((eventData as any)?.status) return (eventData as any).status;
       if (!masterEvent) return "upcoming";
       const today = new Date();
       const startDate = new Date(masterEvent.date);
       const endDate = masterEvent.endDate ? new Date(masterEvent.endDate) : startDate;
       if (today < startDate) return "upcoming";
       if (today > endDate) return "finished";
       return "active";
     })() as "active" | "upcoming" | "finished",
     currentStage: (eventData as any)?.currentStage || consistentData.currentStage,
     tournamentFormat: (() => {
       if ((eventData as any)?.tournamentFormat) return normalizeTournamentFormat((eventData as any).tournamentFormat);
       if (masterEvent && !masterEvent.isLeague) {
         return normalizeTournamentFormat((masterEvent as any).tournamentFormat);
       }
       return "round-robin";
     })() as "round-robin" | "eliminacion-directa" | "sumatoria-juegos",
    nextMatch: {
      opponent: consistentData.nextMatchOpponent,
      court: consistentData.nextMatchCourt,
      time: consistentData.nextMatchTime
    }
  };
  
  console.log('TournamentDetails - eventData found:', eventData);
  console.log('TournamentDetails - Final tournament:', tournament);

  const tournamentPrizes = tournament.prizes || [];
  const nextMatchInfo = getNextMatchInfo(tournament as any);
  const formattedNextMatch = formatNextMatchDisplay(nextMatchInfo);

  const mockFinishedEvents = [
    { id: 26, title: "Torneo Round Robin Finalizado", sport: "Pádel", tournamentFormat: "round-robin", status: "finished" },
    { id: 27, title: "Torneo Eliminación Finalizado", sport: "Pádel", tournamentFormat: "eliminacion-directa", status: "finished" },
    { id: 28, title: "Torneo Sumatoria Finalizado", sport: "Pádel", tournamentFormat: "sumatoria-juegos", status: "finished" }
  ];

  // Mock Round Robin data
  const roundRobinData = {
    userGroup: "Grupo B",
    qualificationRule: "Pasan los 2 primeros de cada grupo",
    groupMatches: [
      { id: 1, player1: "Alex Rodríguez / Carlos Ruiz", player2: "María López / Juan García", score: "6-2, 6-4", court: "Cancha 1", time: "09:00", date: "2025-06-15", status: "completed" as const, isUserMatch: true },
      { id: 2, player1: "Alex Rodríguez / Carlos Ruiz", player2: "Ana Martín / Luis Torres", score: "6-3, 4-6, 7-5", court: "Cancha 2", time: "11:30", date: "2025-06-16", status: "completed" as const, isUserMatch: true },
      { id: 3, player1: "Alex Rodríguez / Carlos Ruiz", player2: "Elena Ruiz / Pedro Sanz", court: "Cancha 3", time: "14:00", date: "2025-06-17", status: "scheduled" as const, isUserMatch: true },
      { id: 4, player1: "María López / Juan García", player2: "Ana Martín / Luis Torres", score: "7-6, 6-4", court: "Cancha 2", time: "10:00", date: "2025-06-16", status: "completed" as const },
      { id: 5, player1: "María López / Juan García", player2: "Elena Ruiz / Pedro Sanz", court: "Cancha 1", time: "15:30", date: "2025-06-17", status: "scheduled" as const },
      { id: 6, player1: "Ana Martín / Luis Torres", player2: "Elena Ruiz / Pedro Sanz", court: "Cancha 2", time: "16:30", date: "2025-06-17", status: "scheduled" as const }
    ],
    groupStandings: [
      { player: "Alex Rodríguez / Carlos Ruiz", matchesPlayed: 2, matchesWon: 2, matchesLost: 0, setsWon: 4, setsLost: 2, gamesWon: 39, gamesLost: 28, points: 6, isUser: true },
      { player: "María López / Juan García", matchesPlayed: 2, matchesWon: 1, matchesLost: 1, setsWon: 3, setsLost: 2, gamesWon: 30, gamesLost: 26, points: 3 },
      { player: "Ana Martín / Luis Torres", matchesPlayed: 2, matchesWon: 1, matchesLost: 1, setsWon: 2, setsLost: 3, gamesWon: 28, gamesLost: 32, points: 3 },
      { player: "Elena Ruiz / Pedro Sanz", matchesPlayed: 0, matchesWon: 0, matchesLost: 0, setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0, points: 0 }
    ]
  };

  const matches: Match[] = [
    { id: 1, round: "Cuartos de Final", player1: "Alex Rodríguez / Carlos Ruiz", player2: "Ana García / Luis Martín", court: "Cancha 3", time: "14:00", date: "2025-06-18", status: "scheduled", isUserMatch: true },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-emerald-100 text-emerald-700';
      case 'in-progress': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled': return t('tournament.scheduled');
      case 'in-progress': return t('tournament.inProgress');
      case 'completed': return t('tournament.completed');
      default: return t('tournament.unknown');
    }
  };

  const getSportColor = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'pádel': return 'bg-emerald-100 text-emerald-800';
      case 'tenis': return 'bg-green-100 text-green-800';
      case 'pickleball': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  const getOrganizerImage = (organizerName: string, sport: string) => {
    const organizerImages: { [key: string]: string } = {
      "Club Las Raquetas": "/lovable-uploads/1bcd1940-66b0-4c8c-aa95-3f6e609dd00b.png",
      "Organizador": "/lovable-uploads/404ad11f-3a8c-4971-8c7f-b2f397498282.png",
      "Club Central": "/lovable-uploads/5b084fa7-1691-4ba9-84d3-9c2aaabd012a.png",
      "Academia Deportiva": "/lovable-uploads/74658797-19bc-45f3-92d8-74c72c9e5ff8.png",
      "Centro Deportivo Elite": "/lovable-uploads/97b25d85-5d30-45c4-9f72-90caca11ddfa.png",
      "Prueba 101": "/lovable-uploads/bb567a48-6ee6-4eea-917e-c2e42d492ea0.png",
      "Test Organizer": "/lovable-uploads/c9c992c0-9cbd-430e-9c26-ed615c5e47dc.png"
    };
    
    if (organizerImages[organizerName]) return organizerImages[organizerName];
    
    switch (sport.toLowerCase()) {
      case 'tenis': return heroTennis;
      case 'pickleball': return heroPickleball;
      case 'pádel':
      default: return heroPadel;
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: tournament.title,
        text: `${t('tournament.joinTournament')} ${tournament.title}!`,
        url: window.location.href,
      }).catch((err) => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success(t('tournament.linkCopied'));
    }
  };

  const userMatches = matches.filter(match => match.isUserMatch);
  const nextMatch = formattedNextMatch;

  const getModality = (format?: string): 'round-robin' | 'elimination' | 'sum-games' => {
    switch (format) {
      case 'round-robin': return 'round-robin';
      case 'eliminacion-directa':
      case 'elimination': return 'elimination';
      case 'sumatoria-juegos': return 'sum-games';
      default: return 'round-robin';
    }
  };

  const getTournamentFormatDisplay = () => {
    const mappedModality = getModality(tournament.tournamentFormat);
    switch (mappedModality) {
      case 'round-robin': return t('tournament.roundRobin');
      case 'elimination': return t('tournament.eliminationDirect');
      case 'sum-games': return t('tournament.sumOfGames');
      default: return t('tournament.roundRobin');
    }
  };

  const renderTournamentView = () => {
    const tournamentData = {
      id: tournament.id,
      title: tournament.title,
      name: tournament.title,
      date: tournament.startDate,
      startDate: tournament.startDate,
      endDate: tournament.endDate,
      location: tournament.location,
      club: tournament.location,
      modality: getModality(tournament.tournamentFormat),
      tournamentFormat: tournament.tournamentFormat,
      gameType: (tournament.matchType === 'dobles') ? 'doubles' as const : 'singles' as const,
      matchType: (tournament.matchType === 'dobles') ? 'doubles' as const : 'singles' as const,
      sport: tournament.sport.toLowerCase() as 'padel' | 'tennis' | 'pickleball',
      category: 'mixtos' as const,
      subcategory: tournament.category,
      partner: tournament.partner,
      currentStage: tournament.currentStage,
      rules: tournament.rules,
      status: tournament.status as 'upcoming' | 'active' | 'finished'
    };

    const dynamicData = generateTournamentData(tournament as any);

    if (tournament.tournamentFormat === 'round-robin') {
      return (
        <TournamentRoundRobinView tournament={tournamentData} />
      );
    }

    const isUpcoming = tournament.status === 'upcoming';
    if (isUpcoming) {
      const modality = getModality(tournament.tournamentFormat);
      
      switch (modality) {
        case 'round-robin':
          if (showPendingBrackets) {
            return (
              <PendingBracketView 
                title={t('tournament.eliminationBracket')}
                sport={tournament.sport}
                gameType={tournamentData.gameType}
                showParticipants={true}
                eventType="tournament"
                eventTitle={tournament.title}
              />
            );
          }
          return (
            <div className="space-y-6">
              <PendingMatchCard 
                title={t('tournament.groupMatches')}
                date={tournament.startDate}
                modalidad={tournamentData.gameType}
                showParticipants={true}
                eventType="tournament"
                eventTitle={tournament.title}
              />
              <PendingRankingTable 
                title={t('tournament.groupPositions')}
                modalidad={tournamentData.gameType}
                type="round-robin"
                onShowBrackets={() => setShowPendingBrackets(true)}
              />
            </div>
          );
        case 'elimination':
          if (registration) {
            return (
              <TournamentEliminationMatchesView
                eventId={String(tournament.id)}
                player1Name={registration.player1_name}
                player2Name={registration.player2_name}
                category={registration.category}
                subcategory={registration.subcategory}
                registrations={registrations}
                tournamentTitle={tournament.title}
                sport={tournament.sport}
              />
            );
          }
          return (
            <PendingBracketView 
              title={t('tournament.eliminationDirectBracket')}
              sport={tournament.sport}
              gameType={tournamentData.gameType}
              showParticipants={true}
              eventType="tournament"
              eventTitle={tournament.title}
            />
          );
        case 'sum-games':
          if (showPendingBrackets) {
            return (
              <PendingBracketView 
                title={t('tournament.eliminationBracket')}
                sport={tournament.sport}
                gameType={tournamentData.gameType}
                showParticipants={true}
                eventType="tournament"
                eventTitle={tournament.title}
              />
            );
          }
          return (
            <div className="space-y-6">
              <PendingMatchCard 
                title={t('tournament.tournamentMatches')}
                date={tournament.startDate}
                modalidad={tournamentData.gameType}
                showParticipants={true}
                eventType="tournament"
                eventTitle={tournament.title}
              />
              <PendingRankingTable 
                title={t('tournament.classificationByGames')}
                modalidad={tournamentData.gameType}
                type="sumatoria-juegos"
                onShowBrackets={() => setShowPendingBrackets(true)}
              />
            </div>
          );
        default:
          if (registration) {
            const eventForCard = {
              id: tournament.id.toString(),
              title: tournament.title,
              sport: tournament.sport || 'Pádel',
              startDate: tournament.startDate,
              location: tournament.location,
              category: tournament.category || 'Sin categoría',
              status: (tournament.status || 'upcoming') as "upcoming" | "active" | "finished"
            };
            return <TournamentProgressCard event={eventForCard} userRegistered={true} />;
          }
          return (
            <PendingStateCard 
              title={t('tournament.registerToSeeDetails')}
              message={t('tournament.registerToSeeDetailsMsg')}
              icon="calendar"
              type="general"
              showParticipants={true}
              gameType={tournamentData.gameType}
              eventType="tournament"
              eventTitle={tournament.title}
            />
          );
      }
    }

    if (registration) {
      const modality = getModality(tournament.tournamentFormat);
      
      if (modality === 'elimination') {
        return (
          <TournamentEliminationMatchesView
            eventId={String(tournament.id)}
            player1Name={registration.player1_name}
            player2Name={registration.player2_name}
            category={registration.category}
            subcategory={registration.subcategory}
            registrations={registrations}
            tournamentTitle={tournament.title}
            sport={tournament.sport}
          />
        );
      }
      
      const eventForCard = {
        id: tournament.id.toString(),
        title: tournament.title,
        sport: tournament.sport || 'Pádel',
        startDate: tournament.startDate,
        location: tournament.location,
        category: tournament.category || 'Sin categoría',
        status: (tournament.status || 'upcoming') as "upcoming" | "active" | "finished"
      };
      return <TournamentProgressCard event={eventForCard} userRegistered={true} />;
    }
    
    let view;
    switch (getModality(tournament.tournamentFormat)) {
      case 'round-robin':
        view = <TournamentRoundRobinView tournament={tournamentData as any} onRegisterClick={handleRegisterClick} onAccessCodeClick={handleAccessCodeClick} isUserRegistered={!!registration} registrationLoading={registrationLoading} />;
        break;
      case 'elimination':
        view = <TournamentEliminationView tournament={tournamentData as any} onRegisterClick={handleRegisterClick} onAccessCodeClick={handleAccessCodeClick} isUserRegistered={!!registration} registrationLoading={registrationLoading} />;
        break;
      case 'sum-games':
        view = <TournamentSumGamesView tournament={tournamentData as any} />;
        break;
      default:
        view = <TournamentRoundRobinView tournament={tournamentData as any} onRegisterClick={handleRegisterClick} onAccessCodeClick={handleAccessCodeClick} isUserRegistered={!!registration} registrationLoading={registrationLoading} />;
    }
    return view;
  };

  const eventForRegistration = {
    id: typeof tournament.id === 'string' ? tournament.id : String(tournament.id),
    title: tournament.title,
    organizer: tournament.organizer,
    sport: tournament.sport,
    date: tournament.startDate,
    endDate: tournament.endDate,
    time: tournament.time,
    location: tournament.location,
    registrationFee: tournament.registrationFee || 0,
    categories: ['mixta'],
    genderCategories: (eventData?.genderCategories && Array.isArray(eventData.genderCategories) && eventData.genderCategories.length > 0)
      ? eventData.genderCategories
      : tournament.genderCategories,
    subcategories: (eventData?.subcategories && typeof eventData.subcategories === 'object' && Object.keys(eventData.subcategories).length > 0)
      ? eventData.subcategories
      : tournament.subcategories,
    participants: 0,
    maxParticipants: 100,
    max_participants: (tournament as any).maxParticipants || 100,
    matchType: tournament.matchType as 'singles' | 'dobles',
    isLeague: false,
    tournamentFormat: tournament.tournamentFormat,
    address: (eventData as any)?.address,
    postal_code: (eventData as any)?.postal_code,
    country: (eventData as any)?.country,
    state: (eventData as any)?.state,
    organizer_id: (eventData as any)?.organizer_id,
    rules: supabaseEvent?.rules || (eventData as any)?.rules,
  };

  // Sport label helper
  const getSportLabel = (sport: string) => {
    const s = sport.toLowerCase();
    if (s === 'padel' || s === 'pádel') return language === 'en' ? 'Padel' : 'Pádel';
    if (s === 'tenis' || s === 'tennis') return language === 'en' ? 'Tennis' : 'Tenis';
    return sport.charAt(0).toUpperCase() + sport.slice(1);
  };

  return (
    <div className={`${isMobile ? 'space-y-4' : 'space-y-6'}`}>
      {/* Hero Image with Overlaid Icons */}
      <div className="relative">
        <div className="relative overflow-hidden rounded-lg">
          <img 
            src={tournament.imageUrl || getOrganizerImage(tournament.organizer, tournament.sport)}
            alt={`${tournament.title} - ${tournament.organizer}`}
            className="w-full h-40 md:h-48 object-cover cursor-pointer"
            onClick={() => setShowImageDialog(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />
          
          <button 
            onClick={onBack}
            className="absolute top-4 left-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </button>
          
          <button 
            onClick={onContactOrganizer}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
            title={t('tournament.contactOrganizer')}
          >
            <MessageCircle className="h-5 w-5 text-white" />
          </button>
          
          <div className={`absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md ${isMobile ? 'p-2' : 'p-3'}`}>
            <div className="flex items-center justify-between text-white mb-1">
              <h1 className={`${isMobile ? 'text-base' : 'text-xl'} font-bold`}>
                {tournament.title}
              </h1>
              <span className="text-xs opacity-90">
                {getSportLabel(tournament.sport)}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-white text-xs">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3" />
                <span className="opacity-90">{tournament.location}</span>
              </div>
              
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                <span>
                  {format(new Date(tournament.startDate), 'd MMM', { locale: dateLocale })} - {format(new Date(tournament.endDate), 'd MMM yyyy', { locale: dateLocale })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tournament Info */}
      <TournamentInfo
        tournament={{
          id: tournament.id,
          name: tournament.title,
          date: tournament.startDate,
          endDate: tournament.endDate,
          modality: (tournament.matchType === 'dobles') ? 'doubles' : 'singles',
          gameType: (tournament.matchType === 'dobles') ? 'doubles' : 'singles',
          sport: tournament.sport.toLowerCase() as 'tennis' | 'padel' | 'pickleball',
          category: tournament.category,
          subcategory: tournament.subcategory || '',
          partner: tournament.partner || '',
          currentStage: tournament.currentStage,
          status: tournament.status,
          location: tournament.location
        }}
        prizes={tournamentPrizes}
        tournamentFormat={tournament.tournamentFormat}
        onContactOrganizer={onContactOrganizer}
        currentView={currentView}
        userRegistration={registration ? {
          category: registration.category,
          subcategory: registration.subcategory
        } : null}
        userRegistrations={registrations?.map(r => ({ id: r.id, category: r.category, subcategory: r.subcategory, partner_name: r.partner_name, partner_access_code: r.partner_access_code }))}
        onRegisterClick={handleRegisterClick}
        onAccessCodeClick={handleAccessCodeClick}
        isUserRegistered={!!registration}
        registrationLoading={registrationLoading}
        partnerAccessCode={registration?.partner_access_code}
      />

      {/* Registration Form / Access Code Entry Section */}
      {showAccessCodeEntry && (
        <div id="registration-section">
          <AccessCodeEntry 
            onBack={() => setShowAccessCodeEntry(false)}
            onSuccess={() => window.location.reload()}
            eventId={tournamentId}
          />
        </div>
      )}
      
      {showRegistration && !showAccessCodeEntry && (
        <div id="registration-section">
          <EventRegistrationForm 
            event={eventForRegistration}
            onBack={() => {}}
            onRegister={() => {}}
          />
        </div>
      )}

      {/* Tournament Bracket/Groups */}
      {getModality(tournament.tournamentFormat) === 'elimination' ? (
        <div className={isMobile ? 'mx-0' : ''}>
          {renderTournamentView()}
        </div>
      ) : (
        <Card className={`bg-white ${isMobile ? 'mx-0' : ''}`}>
          <CardHeader className={isMobile ? 'p-4 pb-2' : ''}>
             <div className={`flex items-center ${isMobile ? 'flex-col gap-2' : 'justify-between'} w-full`}>
               <div className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
                 <Trophy className="h-5 w-5 text-orange-600" />
                 {getModality(tournament.tournamentFormat) === 'round-robin'
                   ? (currentView === 'elimination' || showPendingBrackets ? t('tournament.eliminationPhase') : t('tournament.groupPhase'))
                   : t('tournament.sumOfGames')}
               </div>
               {getModality(tournament.tournamentFormat) === 'round-robin' && (currentView === 'elimination' || showPendingBrackets) && (
                 <Button 
                   variant="outline" 
                   size="sm"
                   onClick={() => {
                     setCurrentView('groups');
                     setShowPendingBrackets(false);
                   }}
                   className="text-slate-600 border-slate-300 hover:bg-slate-50"
                 >
                   {t('tournament.backToGroups')}
                 </Button>
               )}
             </div>
          </CardHeader>
          <CardContent className={isMobile ? 'p-4 pt-2' : ''}>
            {renderTournamentView()}
          </CardContent>
        </Card>
      )}

      {/* Image Fullscreen Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none [&>button]:text-white">
          <img 
            src={tournament.imageUrl || getOrganizerImage(tournament.organizer, tournament.sport)}
            alt={tournament.title}
            className="w-full h-full object-contain max-h-[90vh]"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentDetails;
